import Button from '@mui/material/Button';
import {
  Box, TextField, Typography, Stack, Grid, Container,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { openSnackbar } from '../../../../features/SnackBar/snackBarSlice';
import useToggle from '../../../../hooks/useToggle';
import {
  addFeature, getCustomEntityList, getFeatureDetailList, getKpiPreview, updateFeature,
} from '../../../../features';
import useStyles from '../../../../hooks/useStyles';
import styles from '../../../../layouts/pages/style';
import {
  customEntityChildren, customEntityIcons, customEntityKeys, customEntityTypes, nestedFeature,
} from '../../constants';
import { PickCustomEntity } from '../PickCustomEntity';
import { CustomComponents } from '../CustomComponents';
import { KPIPreview } from '../KPIPreview';
import { RefineFilter } from '../RefineFilter';

function FeatureManager() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const classes = useStyles(styles)();
  const isCopyClick = location.state?.isCopyClick ?? false;

  const [selectedEntities, setSelectedEntities] = useState({});
  const [selectedNestedFeatures, setSelectedNestedFeatures] = useState([]);
  const [selectedEntityKey, setSelectedEntityKey] = useState(null);
  const [featureName, setFeatureName] = useState('');
  const [previewData, setPreviewData] = useState({});
  const [openPreview, setOpenPreview] = useToggle();
  const [refineFilter, setRefineFilter] = useState(null);
  const featureDetails = useSelector((state) => state.featureDetailList);
  const featuresList = useSelector((state) => state.featuresList?.response?.responseArray);

  const handleRefineFilter = (entity) => {
    setRefineFilter(entity);
  };

  const handleCloseRefineFilter = () => {
    setRefineFilter(null);
  };

  const handleRefineFilterSave = (entity, operations, eventSource) => {
    if (operations?.length > 0) {
      setSelectedEntities((prev) => {
        const updated = { ...prev };

        updated[customEntityKeys.CUSTOM_OBJECT] = updated[customEntityKeys.CUSTOM_OBJECT].map((item) => (item.custom_entity_id === entity.custom_entity_id
          ? { ...item, operation_type: operations, event_source_type: eventSource }
          : item));

        return updated;
      });

      setRefineFilter(null);
    }
  };

  const handleNameChange = (e) => {
    setFeatureName(e.target.value);
  };

  const handleOpenPick = (entityKey, keyName) => {
    setSelectedEntityKey({ key: entityKey, name: keyName });
    if (entityKey !== customEntityKeys.NESTED_FEATURE) {
      dispatch(getCustomEntityList({ type: customEntityChildren[entityKey] }));
    }
  };

  const handleClosePick = () => {
    setSelectedEntityKey(null);
  };

  // Add entity to the selected list for a given typeId
  const handlePickEntity = (typeId, entities) => {
    if (typeId === customEntityKeys.NESTED_FEATURE) {
      setSelectedNestedFeatures(entities);
    } else {
      setSelectedEntities((prev) => ({ ...prev, [typeId]: entities }));
    }
  };

  // Remove entity from a given typeId
  const handleRemoveEntity = (typeId, entityId) => {
    setSelectedEntities((prev) => ({
      ...prev,
      [typeId]: (prev[typeId] || []).filter((e) => e.custom_entity_id !== entityId),
    }));
  };

  const handleRemoveNestedFeature = (_, featureId) => {
    setSelectedNestedFeatures((prev) => prev.filter((f) => f.nested_feature_id !== featureId));
  };

  const handlePreviewBtn = () => {
    const saveData = {};
    saveData.featureName = featureName;
    saveData.customEntityObjects = {
      ...selectedEntities,
      NestedFeatures: selectedNestedFeatures,
    };
    dispatch(getKpiPreview({ data: saveData })).then((res) => {
      if (res.error) {
        dispatch(openSnackbar({ open: true, message: 'Whoops! Something went wrong.', type: 'error' }));
      } else {
        setOpenPreview(true);
        setPreviewData(res?.payload ?? {});
      }
    });
  };

  const handleSaveBtn = () => {
    const saveData = {};
    saveData.featureName = featureName;
    saveData.customEntity = Object.values(selectedEntities).flat();
    saveData.nestedFeatures = selectedNestedFeatures;
    saveData.featureId = id;
    if (!saveData.featureId || isCopyClick) {
      dispatch(addFeature({ data: saveData })).then((res) => {
        const { payload } = res;
        if (res.error) {
          dispatch(openSnackbar({ open: true, message: payload, type: 'error' }));
        } else {
          dispatch(openSnackbar({ open: true, message: 'Feature Created Successfully', type: 'success' }));
          navigate('/feature-manager');
        }
      });
    } else {
      dispatch(updateFeature(saveData)).then((res) => {
        const { payload } = res;
        if (res.error) {
          dispatch(openSnackbar({ open: true, message: payload, type: 'error' }));
        } else {
          dispatch(openSnackbar({ open: true, message: 'Feature Updated Successfully', type: 'success' }));
          navigate('/feature-manager');
        }
      });
    }
  };

  // Populate selectedEntities from API data
  useEffect(() => {
    if (!id) {
      setFeatureName('');
      setSelectedEntities({});
      setSelectedNestedFeatures([]);
      return;
    }

    if (featureDetails?.featureName) {
      setFeatureName(featureDetails.featureName);
    }
    if (featureDetails?.customEntity) {
      const mapped = {};
      featureDetails.customEntity.forEach((entity) => {
        const entityCopy = { ...entity };
        const parentTypeId = Object.keys(customEntityChildren).find((typeKey) => customEntityChildren[typeKey]?.includes(entity.custom_entity_type));

        if (parentTypeId) {
          if (parentTypeId !== customEntityKeys.CUSTOM_OBJECT) {
            delete entityCopy.operation_type;
            delete entityCopy.event_source_type;
          } else {
            entityCopy.operation_type = entityCopy.operation_type ? JSON.parse(entityCopy.operation_type) : [];
          }

          if (!mapped[parentTypeId]) mapped[parentTypeId] = [];
          mapped[parentTypeId].push(entityCopy);
        }
      });
      setSelectedEntities(mapped);
    }
    if (featureDetails?.nestedFeatures) {
      const filterData = featureDetails.nestedFeatures.map((nf) => ({
        nested_feature_id: nf.nested_feature_id,
        nested_feature_name: nf.nested_feature_name || '',
      }));

      setSelectedNestedFeatures(filterData);
    }
  }, [id, featureDetails, featuresList]);

  useEffect(() => {
    if (id) dispatch(getFeatureDetailList(id));
  }, [dispatch, id]);

  return (
    <Container maxWidth="xl">
      <Grid container gap={2}>
        <Grid item xs={12} className={classes.cardStyles}>
          <Box className={classes.headerStyles}>
            <Typography variant="h4">Feature</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} className={classes.cardStyles}>
          <Stack style={{ padding: '10px 0px' }}>
            <Typography>Feature Label</Typography>
            <TextField
              value={featureName}
              onChange={handleNameChange}
              id="outlined-basic"
              fullWidth
              type="text"
              placeholder="Required*"
              variant="outlined"
              required
              focused={isCopyClick}
              autoFocus={isCopyClick}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} key={nestedFeature.id} className={classes.cardStyles}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="start"
            background="#919eab3d"
            borderColor="#919eab3d"
            spacing={1.5}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleOpenPick(nestedFeature.id, nestedFeature.name)}
            >
              Pick
            </Button>
            <Typography>Nested Features</Typography>
          </Stack>

          {selectedNestedFeatures?.length > 0 && (
          <CustomComponents
            data={selectedNestedFeatures}
            handleRemove={handleRemoveNestedFeature}
            type={nestedFeature.id}
            customIcon={customEntityIcons.NestedFeature}
          />
          )}
        </Grid>

        {customEntityTypes.map((obj) => (
          <Grid item xs={12} key={obj.id} className={classes.cardStyles}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="start"
              background="#919eab3d"
              borderColor="#919eab3d"
              spacing={1.5}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleOpenPick(obj.id, obj.name)}
              >
                Pick
              </Button>
              <Typography>{obj.name}</Typography>
            </Stack>

            {selectedEntities[obj.id]?.length > 0 && (
            <CustomComponents
              data={selectedEntities[obj.id]}
              handleRemove={handleRemoveEntity}
              type={obj.id}
              customIcon={customEntityIcons[obj.id]}
              handleRefineFilter={handleRefineFilter}
            />
            )}
          </Grid>
        ))}

        <Grid item xs={12}>
          <Stack
            spacing={1}
            direction="row"
            alignItems="center"
            justifyContent="end"
            sx={{ pt: 2 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/feature-manager')}
            >
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handlePreviewBtn}>
              Preview
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveBtn}>
              Save
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {selectedEntityKey !== null && (
      <PickCustomEntity
        open={selectedEntityKey !== null}
        onClose={handleClosePick}
        onSave={handlePickEntity}
        initialSelected={selectedEntities[selectedEntityKey.key]}
        initialNestedFeatures={selectedNestedFeatures}
        entityKey={selectedEntityKey.key}
        label={selectedEntityKey.name}
        featureId={id}
      />
      )}

      {refineFilter !== null && (
      <RefineFilter
        open={refineFilter !== null}
        customFilter={refineFilter}
        handleClose={handleCloseRefineFilter}
        handleRefineFilterSave={handleRefineFilterSave}
      />
      )}

      {openPreview && (
      <KPIPreview
        open={openPreview}
        previewData={previewData}
        handleClose={setOpenPreview}
      />
      )}
    </Container>
  );
}

export default FeatureManager;
